import React from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import ThemedButton from '../Shared/ThemedButton';
import TravelFeeTooltip from './TravelFeeTooltip';
import PoliciesAgreementNote from './PoliciesAgreementNote';
import { getBookingPageTheme } from '../../utility/themeUtils';
import { isDarkMode } from '../../utility/themeVars';
import { formatPriceWithCurrency, parsePrice } from '../../utility/priceUtils';
import { formatDuration, formatDateTime, getLocationDisplay } from '../../utility/bookingUtils';
import { requiresApproval, calculateBookingTotals, getContinueButtonText } from '../../utility/bookingFlowUtils';
import { useBookingFlow } from '../../contexts/BookingFlowContext';

const BookingWorkflowSidebar = ({
  className = '',
  sticky = false,
}) => {
  const {
    bookingPage,
    selectedServices,
    onContinue,
    continueDisabled,
    travelFee,
    selectedAppointmentType,
    currency,
    outsideAvailabilityRequested,
    customTimeRequested,
    currentBookingStep,
    selectedDate,
    selectedTime,
    selectedAddress,
    selectedLocation,
    fixedLocationData,
    currentBookingError,
    validationErrors,
    contactFormValidationFailed,
    isSubmittingBooking,
    updateServiceQuantity,
  } = useBookingFlow();

  const canEditQuantity = currentBookingStep === 'services';

  const { primaryColor, themeMode } = getBookingPageTheme(bookingPage);
  const isDark = isDarkMode(themeMode);

  const approvalRequired = requiresApproval(outsideAvailabilityRequested, customTimeRequested, bookingPage?.autoApproval);
  const { serviceTotal, totalPrice, totalItems, applicableTravelFee } = calculateBookingTotals(
    selectedServices,
    selectedAppointmentType,
    bookingPage?.travelFeesEnabled,
    travelFee
  );

  // When sticky (desktop), cap the card to the viewport and split it into a
  // scrollable body + pinned footer so the CTA remains visible even when the
  // summary is tall (many services, travel fee, approval notice, errors).
  // When not sticky (mobile / inline), render as a plain card with its
  // natural height.
  const cardClass = sticky
    ? 'bg-themed-card border-themed rounded-card shadow-lift border flex flex-col lg:max-h-[calc(100vh-3rem)] overflow-hidden'
    : 'bg-themed-card border-themed rounded-card shadow-lift border p-6';
  const bodyClass = sticky ? 'lg:flex-1 lg:overflow-y-auto p-6' : '';
  const footerClass = sticky
    ? 'border-themed border-t p-6 flex-shrink-0 bg-themed-card'
    : 'border-themed border-t pt-4';

  const hasBookingSummary = currentBookingStep !== 'services' && (selectedDate || selectedTime || selectedAddress || selectedLocation);
  const hasValidationErrors = validationErrors && Object.keys(validationErrors).length > 0;
  const hasErrorMessage = currentBookingError || hasValidationErrors || contactFormValidationFailed;

  return (
    <div className={`space-y-6 w-full ${className} ${sticky ? 'lg:sticky lg:top-6 lg:self-start' : ''}`}>
      {/* Booking Summary Card */}
      <div className={cardClass}>
        {/* Scrollable body: summary, services, travel fee, approval notice */}
        <div className={bodyClass}>
          {/* Booking Summary — shows progressively as selections are made */}
          {hasBookingSummary && (
            <div className="mb-6">
              <p className="smallcaps text-themed-secondary mb-3">Your booking</p>
              <div className="space-y-1.5 text-sm">
                {selectedDate && selectedTime && (
                  <div className="flex items-start gap-3">
                    <span className="text-themed-secondary min-w-[52px]">When</span>
                    <span className="text-themed-primary">{formatDateTime(selectedDate, selectedTime)}</span>
                  </div>
                )}
                {(selectedAddress || selectedLocation) && (
                  <div className="flex items-start gap-3">
                    <span className="text-themed-secondary min-w-[52px]">Where</span>
                    <span className="text-themed-primary">{getLocationDisplay(selectedAppointmentType, selectedAddress, selectedLocation, fixedLocationData)}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Selected Services Summary */}
          <div className={hasBookingSummary ? 'border-themed border-t pt-6' : ''}>
            <div className="flex items-baseline justify-between mb-3">
              <span className="smallcaps text-themed-secondary">Your selection</span>
              <span className="text-xs text-themed-secondary tabular">
                {totalItems} {totalItems === 1 ? 'item' : 'items'}
              </span>
            </div>

            {/* Services List — hairline rhythm, no boxes */}
            <ul className="mb-5 [&>li+li]:border-t [&>li+li]:border-themed">
              {selectedServices.map(({ service, quantity }) => (
                <li key={service._id} className="flex items-start justify-between gap-3 py-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-themed-display text-base text-themed-primary leading-snug truncate">
                      {service.name}
                    </p>
                    <p className="text-xs text-themed-secondary mt-0.5 tabular">
                      {formatDuration(service.duration * quantity)}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1.5 shrink-0">
                    <p className="font-themed-display text-base text-themed-primary tabular leading-none">
                      {formatPriceWithCurrency(parsePrice(service.price) * quantity, currency)}
                    </p>
                    {canEditQuantity && (
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          aria-label={quantity === 1 ? `Remove ${service.name}` : `Decrease quantity of ${service.name}`}
                          onClick={() => updateServiceQuantity(service, Math.max(0, quantity - 1))}
                          className="w-6 h-6 rounded-full border flex items-center justify-center transition-colors duration-200 ease-editorial hover-themed-tint focus-themed focus:outline-none"
                          style={{
                            borderColor: 'var(--theme-primary)',
                            color: 'var(--theme-primary)',
                          }}
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M20 12H4" />
                          </svg>
                        </button>
                        <span className="min-w-[18px] text-center text-sm font-medium text-themed-primary tabular">
                          {quantity}
                        </span>
                        <button
                          type="button"
                          aria-label={`Increase quantity of ${service.name}`}
                          onClick={() => updateServiceQuantity(service, quantity + 1)}
                          className="w-6 h-6 rounded-full border flex items-center justify-center transition-colors duration-200 ease-editorial hover-themed-tint focus-themed focus:outline-none"
                          style={{
                            borderColor: 'var(--theme-primary)',
                            color: 'var(--theme-primary)',
                          }}
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                        </button>
                      </div>
                    )}
                    {!canEditQuantity && quantity > 1 && (
                      <p className="text-xs text-themed-secondary tabular">
                        Qty {quantity}
                      </p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Travel Fee — same price column as services, with a moss left edge */}
          {applicableTravelFee > 0 && (
            <div className="flex items-start justify-between gap-4 py-3 pl-3 border-l-2 border-themed-accent mb-4">
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <p className="text-sm font-medium text-themed-primary">
                    {outsideAvailabilityRequested ? 'Estimated travel fee' : 'Travel fee'}
                  </p>
                  <TravelFeeTooltip bookingPage={bookingPage} isDark={isDark} />
                </div>
                <p className="text-xs text-themed-secondary mt-0.5">
                  {outsideAvailabilityRequested
                    ? 'Subject to approval'
                    : 'Mobile service delivery'}
                </p>
              </div>
              <span className="font-themed-display text-base text-themed-primary tabular shrink-0">
                {formatPriceWithCurrency(applicableTravelFee, currency)}
              </span>
            </div>
          )}

          {/* Approval Required Indicator — amber left edge, no chunky box */}
          {approvalRequired && (
            <div className="border-l-2 border-signal-amber-500 pl-3 py-2 flex items-start gap-2">
              <ExclamationTriangleIcon
                aria-hidden="true"
                className="w-4 h-4 text-signal-amber-500 mt-0.5 flex-shrink-0"
              />
              <div className="min-w-0">
                <p className="text-sm font-medium text-themed-primary">
                  This booking will require approval
                </p>
                <p className="text-xs text-themed-secondary mt-0.5">
                  {customTimeRequested && outsideAvailabilityRequested
                    ? 'Custom time and outside service area requested'
                    : customTimeRequested
                      ? 'Custom time requested'
                      : outsideAvailabilityRequested
                        ? 'Address is outside normal service area'
                        : 'All appointments require manual approval'
                  }
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Pinned footer: subtotal, total, error, CTA, helper text */}
        <div className={footerClass}>
          {/* Subtotal and Total */}
          <div className="mb-4">
            {applicableTravelFee > 0 && (
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-themed-secondary">Subtotal</span>
                <span className="text-xs text-themed-secondary tabular">
                  {formatPriceWithCurrency(serviceTotal, currency)}
                </span>
              </div>
            )}
            <div className="flex items-baseline justify-between">
              <span className="smallcaps text-themed-secondary">
                {outsideAvailabilityRequested ? 'Estimated total' : 'Total'}
              </span>
              <span className="font-themed-display text-3xl text-themed-primary tabular leading-none">
                {formatPriceWithCurrency(totalPrice, currency, true)}
              </span>
            </div>
          </div>

          {/* Error Message */}
          {hasErrorMessage && (
            <div className="mb-4 p-3 bg-signal-red-50 border border-signal-red-100 rounded-card">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-signal-red-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-2">
                  <div className="text-sm text-signal-red-700">
                    {currentBookingError === "Unable to process booking with this contact information" ? (
                      <div>
                        <p className="font-medium">{currentBookingError}</p>
                        <p className="mt-1 text-signal-red-600">
                          Please contact us directly to schedule your appointment.
                        </p>
                      </div>
                    ) : contactFormValidationFailed && !currentBookingError && !hasValidationErrors ? (
                      <p>Please fix the errors in the contact form</p>
                    ) : (
                      <div>
                        {currentBookingError}
                        {hasValidationErrors && (
                          <ul className="mt-1 list-disc list-inside">
                            {Object.entries(validationErrors).map(([field, message]) => (
                              <li key={field}>{message}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          <ThemedButton
            onClick={onContinue}
            primaryColor={primaryColor}
            themeMode={themeMode}
            size="large"
            className="w-full"
            disabled={continueDisabled}
          >
            {getContinueButtonText(currentBookingStep, approvalRequired, isSubmittingBooking)}
          </ThemedButton>
          {currentBookingStep === 'contact-confirmation' && (
            <PoliciesAgreementNote bookingPage={bookingPage} />
          )}
          {currentBookingStep === 'services' && continueDisabled && (
            <p className="text-xs text-center text-themed-secondary mt-2">Select at least one service to continue</p>
          )}
          {approvalRequired && currentBookingStep === 'contact-confirmation' && (
            <p className="text-xs text-center text-themed-secondary mt-2">Your booking will be reviewed and confirmed via email</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingWorkflowSidebar;
